
import { GoogleGenAI, GenerateContentResponse } from "@google/genai";
import { SYSTEM_INSTRUCTION } from "../constants";

// Initialize Gemini API client
const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });

export interface ChatHistoryItem {
  role: 'user' | 'model';
  parts: Array<{ text?: string; inlineData?: { mimeType: string; data: string } }>;
}

// We maintain a simple history state to pass to the model for context
let chatSession: any = null;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Fallback model configuration
const PRIMARY_MODEL = 'gemini-3.1-pro-preview';
const FALLBACK_MODEL = 'gemini-3.1-flash-preview'; // Updated to valid model

async function retryWithBackoff<T>(fn: () => Promise<T>, retries = 4, initialDelay = 2000): Promise<T> {
  let attempt = 0;
  while (attempt < retries) {
    try {
      return await fn();
    } catch (error: any) {
      attempt++;
      console.warn(`Gemini API Attempt ${attempt} failed.`);
      
      const status = error?.status || error?.code || error?.error?.code || error?.error?.status;
      const message = error?.message || error?.error?.message || JSON.stringify(error);
      
      // Explicitly check for TypeError which is what fetch throws on network errors
      const isNetworkError = error instanceof TypeError || 
                             (error.name === 'TypeError' && message.includes('fetch'));

      const isTransient = isNetworkError ||
                           status === 503 || 
                           status === 500 || 
                           status === 429 ||
                           status === 'INTERNAL' ||
                           status === 'UNAVAILABLE' || 
                           (typeof message === 'string' && (
                             message.toLowerCase().includes('overloaded') ||
                             message.toLowerCase().includes('internal server error') ||
                             message.toLowerCase().includes('resource exhausted') ||
                             message.toLowerCase().includes('timeout') ||
                             message.toLowerCase().includes('failed to fetch') ||
                             message.toLowerCase().includes('network')
                           ));
      
      if (isTransient && attempt < retries) {
        const jitter = Math.random() * 1000;
        const waitTime = (initialDelay * Math.pow(2, attempt - 1)) + jitter;
        console.warn(`Gemini API transient error (${isNetworkError ? 'Network/TypeError' : status}). Retrying in ${Math.round(waitTime)}ms...`);
        await delay(waitTime);
        continue;
      }
      throw error;
    }
  }
  throw new Error("Max retries exceeded");
}

export const initializeChat = async (initialContext: string) => {
  try {
    if (!process.env.API_KEY) {
      console.error("API Key is missing. Please check API_KEY in your .env file.");
      return false;
    }
    
    // Initialize with Primary Model
    chatSession = ai.chats.create({
      model: PRIMARY_MODEL,
      config: {
        systemInstruction: SYSTEM_INSTRUCTION + "\n\n初始项目数据:\n" + initialContext,
      }
    });
    return true;
  } catch (error) {
    console.error("Failed to initialize chat:", error);
    return false;
  }
};

export const sendMessageToGemini = async (
  message: string, 
  imageBase64?: string,
  mimeType: string = "image/png"
): Promise<string> => {
  // If session died or wasn't init, try to re-init (though this usually requires context)
  if (!chatSession) {
     throw new Error("Chat session not initialized");
  }

  if (!message && !imageBase64) {
      console.warn("sendMessageToGemini called with empty message. Using default spacer.");
      message = "请继续执行下一步分析。"; 
  }

  // Construct Payload
  let messagePayload: string | Array<any> = message || " "; 
  if (imageBase64) {
      messagePayload = [
        { text: message || "请分析这张图片" },
        {
          inlineData: {
            mimeType: mimeType, 
            data: imageBase64
          }
        }
      ];
  }
  
  const requestOptions: any = { message: messagePayload };

  const performCall = async () => {
      try {
          // 1. Try Primary Chat Session with Streaming to prevent timeouts
          const stream = await chatSession.sendMessageStream(requestOptions);
          let fullText = "";
          let groundingSources = "";
          for await (const chunk of stream) {
              const c = chunk as GenerateContentResponse;
              if (c.text) {
                  fullText += c.text;
              }
              const chunks = c.candidates?.[0]?.groundingMetadata?.groundingChunks;
              if (chunks && chunks.length > 0) {
                  const sources = chunks
                      .map((ch: any) => ch.web ? `- [${ch.web.title}](${ch.web.uri})` : null)
                      .filter(Boolean)
                      .join('\n');
                  if (sources) {
                      groundingSources = sources;
                  }
              }
          }
          if (groundingSources) {
              fullText += `\n\n### 🔗 参考来源 (Grounding Sources)\n${groundingSources}`;
          }
          return fullText || "No response generated.";
      } catch (primaryError: any) {
          console.warn(`Primary model ${PRIMARY_MODEL} failed. Attempting fallback to ${FALLBACK_MODEL}...`);
          console.debug("Primary Error Details:", primaryError);
          
          // 2. Fallback: Use generateContent with the System Instruction + Current Prompt
          // Note: We lose the intermediate chat history in this fallback for this specific turn, 
          // but it keeps the app alive.
          
          try {
             const fallbackStream = await ai.models.generateContentStream({
                 model: FALLBACK_MODEL,
                 contents: typeof messagePayload === 'string' ? messagePayload : { parts: messagePayload }, 
                 config: {
                     systemInstruction: SYSTEM_INSTRUCTION,
                 }
             });
             
             let text = "";
             for await (const chunk of fallbackStream) {
                 const c = chunk as GenerateContentResponse;
                 if (c.text) text += c.text;
             }
             text += `\n\n*(注: 由于主模型负载过高，本条回复由备用模型 ${FALLBACK_MODEL} 生成，可能略显简洁)*`;
             return text;
          } catch (fallbackError) {
              console.error("Fallback model also failed:", fallbackError);
              throw primaryError; // Throw the original error if fallback also fails
          }
      }
  };

  try {
    return await retryWithBackoff(performCall);
  } catch (error: any) {
    console.error("Gemini API Final Error:", error);
    
    // Handle TypeError explicitly (like Failed to fetch)
    if (error instanceof TypeError) {
      return `⚠️ AI 服务暂时不可用 (网络错误): ${error.message}。\n\n**建议操作**：\n1. 点击下方“纠错”或“重试”按钮。\n2. 检查网络连接是否稳定。\n3. 如果上传了文件，请尝试减小文件大小。`;
    }

    const status = error?.status || error?.code || error?.error?.code;
    const errorMsg = error?.message || error?.error?.message || 'Unknown Error';
    
    if ((typeof errorMsg === 'string' && errorMsg.includes('User location is not supported')) || status === 'FAILED_PRECONDITION') {
        return `⚠️ **API 区域限制错误**\n\nGemini API 目前未在中国大陆地区开放。检测到您的请求位置受限。\n\n**建议解决方案**：\n1. 请检查您的网络环境（是否开启了全局代理/VPN）。\n2. 确保节点选择为美国、新加坡或日本等支持地区。\n3. 刷新页面重试。`;
    }

    return `⚠️ AI 服务暂时不可用 (${status || 500}): ${errorMsg}。\n\n**建议操作**：\n1. 点击下方“纠错”或“重试”按钮。\n2. 检查网络连接。\n3. 如果上传了文件，请尝试减小文件大小。`;
  }
};

function processResponse(result: GenerateContentResponse): string {
    let responseText = result.text || "No response generated.";

    const groundingChunks = result.candidates?.[0]?.groundingMetadata?.groundingChunks;
    if (groundingChunks && groundingChunks.length > 0) {
        const sources = groundingChunks
            .map((chunk: any) => chunk.web ? `- [${chunk.web.title}](${chunk.web.uri})` : null)
            .filter(Boolean)
            .join('\n');
        
        if (sources) {
            responseText += `\n\n### 🔗 参考来源 (Grounding Sources)\n${sources}`;
        }
    }
    return responseText;
}

/**
 * Generate Floor Plan using Nano Banana Pro (gemini-3-pro-image-preview)
 */
export const generateAIFloorPlan = async (
    originalPlanBase64: string,
    styleRefBase64: string | undefined,
    designBrief: string,
    customPrompt: string = ""
): Promise<string> => {
    try {
        const modelId = 'gemini-3-pro-image-preview';
        
        // CLEANUP: Extract only specific unit mix/layout instructions
        const simplifiedBrief = designBrief.includes("户型") 
            ? designBrief.substring(designBrief.indexOf("户型"), designBrief.indexOf("公区") > -1 ? designBrief.indexOf("公区") : 1000) 
            : designBrief.substring(0, 500);

        const textPrompt = `
        You are an expert architect. 
        Task: Redesign the interior partition and furniture layout of the attached floor plan image (the first image).
        
        CRITICAL RULES:
        1. **ASPECT RATIO LOCK**: You MUST NOT change the aspect ratio or dimensions of the image. The output must match the input shape exactly.
        2. **STRUCTURAL INTEGRITY**: The black/dark lines in the original image are CONCRETE WALLS. Do NOT move or delete them.
        3. **UNIT MIX**: Fill the empty spaces with apartment units based strictly on this mix:
           ${simplifiedBrief}
           ${customPrompt ? `EXTRA USER INSTRUCTIONS: ${customPrompt}` : ""}
        4. **MANDATORY FURNITURE**: Every single unit MUST contain: 
           - A Bed (Double or Single)
           - A Private Bathroom (Toilet + Shower)
           - A Wardrobe
           - A small desk
        5. **LOFT/DUPLEX LOGIC**: If the input mentions "Loft" or "Duplex", please generate the layout showing two levels side-by-side (Level 0 and Level +1.2m) or clearly indicate the mezzanine.
        
        Generate the plan with clean architectural lines, showing furniture clearly.
        `;

        const parts: any[] = [
            {
                inlineData: {
                    mimeType: 'image/png',
                    data: originalPlanBase64
                }
            },
            {
                text: textPrompt
            }
        ];

        if (styleRefBase64) {
            parts.splice(1, 0, {
                inlineData: {
                    mimeType: 'image/png',
                    data: styleRefBase64
                }
            });
        }

        const response = await ai.models.generateContent({
            model: modelId,
            contents: { parts: parts },
            config: {
                imageConfig: {
                    // imageSize: "1K"
                }
            }
        });

        for (const candidate of response.candidates || []) {
            for (const part of candidate.content.parts) {
                if (part.inlineData && part.inlineData.data) {
                    return part.inlineData.data;
                }
            }
        }
        
        throw new Error("No image generated by the model.");

    } catch (error: any) {
        console.error("AI Floor Plan Generation Error:", error);
        throw error;
    }
};
